/**
 * Created by Yoyo Fang on 12/04/2018.
 * Description:
 * Email Digest component
 *
 * ------ maintenance history ------
 * Updated by Abner Sui on 05/10/201 Add function for button 'New Email Digest'
 * Updated by Ella Ma on 09/4/2019 upgrade grid
 * Updated by Daniel Wang on 3/15/2021 Add type column
 * Updated by Daniel Wang on 1/13/2023 Moved email digest list to main menu.
 */

import { AppState } from '../../redux';
import { ColDef, GridOptions } from 'ag-grid-community';
import { Component, HostListener, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';

import { Load as LoadEmailDigestAction, EmailDigestActionTypes } from '../../redux/actions/email-digest.actions';
import { ScheduleHelperService } from '../../services/schedule-helper.service';
import { EmailDigestActionsComponent } from './email-digest-actions.component';
import { emailDigestListSelector } from '../../redux/reducers/email-digest.reducer';
import { filter } from 'rxjs/operators';
import { EmailDigestDialogActions, EmailDigestDialogEvents, EmailGridContextMenu } from './email-digest.model';
import { StoreQuerierService } from '../../services/store-querier.service';
import { EmailDigestService } from '../../services/email-digest.service';
import { Router } from '@angular/router';
import * as lodash from 'lodash';
import { lastCustomActionSelector } from '../../redux/reducers/action-history.reducer';
import { ToastService } from '../../widgets/toast/toast.service';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { EmailDigestSchedulerInfoComponent } from './email-digest-scheduler-info.component';
import { EmailDigestType, DashboardPrintSetting, Layout } from '../../tamalelibs/models/emaildigest.model';
import * as _ from 'lodash';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { LayoutService } from '../../services/layout.service';

export const DEFAULT_PAGE_SIZE = 100;

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'email-digest-list',
    templateUrl: './email-digest-list.component.html',
    styleUrls: ['./email-digest-list.component.scss']
})
export class EmailDigestListComponent implements OnInit, OnDestroy {
    @Input() collapseSuject: Subject<any>;

    columnDefs: Array<ColDef>;
    gridOptions: GridOptions;
    loadingMore = false;
    reloading = false;
    selectedItemMap: Map<string, number> = new Map();
    noDataStyle = 'none';
    rowData: Array<any> = [];
    contextMenuConfig: ContextMenuConfig;
    gridContextMenuConfig: ContextMenuConfig;

    private _destroySubscriptions: Array<Subscription> = [];
    private _gridRowSubject$ = new Subject();
    private _gridApi;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _layoutService: LayoutService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _emailDigestService: EmailDigestService,
        private _router: Router,
        private _toast: ToastService,
    ) {
    }

    ngOnInit() {
        if (!this._changeDetectorRef['destroyed']) {
            // set the top of the sub-chrome, neen't sub-chrome in emial digest list, so set top as 55px (119-64)
            this._layoutService.changeSubTitleTopOpen$.next('64px');
        }
        this._destroySubscriptions.push(
            this._store.pipe(
                select(emailDigestListSelector),
                filter(items => !!items)
            ).subscribe(result => this._onEmailDigestListLoaded(result)),
            this._emailDigestService.dialogFeedbackSubject$.subscribe(event => this._onCreateEmailDigestDetail(event)),
            this._store.pipe(
                select(lastCustomActionSelector),
                filter(item => item.type.indexOf('[EMAILDIGEST]') > -1),
            ).subscribe(action => {
                if (action.type === EmailDigestActionTypes.SWITCH_ACTIVE_SUCCESS) {
                    this._toast.notify({
                        style: NotificationStyles.Success,
                        message: action.payload.name + (action.payload.active ? ' activated' : ' deactivated') + ' successfully',
                    });
                } else if (action.type === EmailDigestActionTypes.SWITCH_ACTIVE_FAILED) {
                    this._toast.notify({
                        style: NotificationStyles.Error,
                        message: action.payload.name + (action.payload.active ? ' activated' : ' deactivated') + '  failed',
                    });
                } else if (action.type === EmailDigestActionTypes.SWITCH_SUBSCRIPTION_SUCCESS) {
                    const popMsg = action.payload.isSubscribed === true ? ' subscribed' : ' unsubscribed';
                    this._toast.notify({
                        style: NotificationStyles.Success,
                        message: action.payload.name + popMsg + ' successfully',
                    });
                } else if (action.type === EmailDigestActionTypes.SWITCH_SUBSCRIPTION_FAILED) {
                    const popMsg = action.payload.isSubscribed === true ? ' subscribed' : ' unsubscribed';
                    this._toast.notify({
                        style: NotificationStyles.Error,
                        message: action.payload.name + popMsg + ' failed',
                    });
                }
            }),
            this._gridRowSubject$.subscribe((event) => {
                if (event['type'] === EmailDigestDialogActions.COPY || event['type'] === EmailDigestDialogActions.EDIT) {
                    this._editDashboardEmailDigest(event);
                } else if (event['type'] === 'UPDATE_GRID_ROW') {
                    const itemsToUpdate = [];
                    this._gridApi.forEachNodeAfterFilterAndSort((rowNode) => {
                        let data = rowNode.data;
                        if (data.id === event['payload'].id) {
                            // must use assign, data = event.paylod is not work
                            data = Object.assign(data, event['payload']);
                            itemsToUpdate.push(data);
                        }
                    });
                    if (itemsToUpdate.length > 0) {
                        // ag-grid v23.1.0 rename updateRowData to applyTransaction
                        this._gridApi.updateRowData({ update: itemsToUpdate });
                    }
                } else if (event['type'] === 'ADD_GRID_ROW') {
                    this._gridApi.updateRowData({ add: event['payload'] });
                } else if (event['type'] === EmailGridContextMenu.SHOW) {
                    this.gridContextMenuConfig.items = event['payload'].data;
                    this.gridContextMenuConfig.show$.next(new ContextMenuShowAction(event['payload'].target, ''));
                }
            }),
        );
        this._store.dispatch(new LoadEmailDigestAction());
        // Defining data for columns
        const cellStyleHandler = (params) => {
            const styles = {
                'white-space': 'nowrap',
                'overflow': 'hidden',
                'text-overflow': 'ellipsis',
                'display': 'block',
                'border': 'none',
            };
            if (!params.data.active) {
                return Object.assign(styles, { 'color': '#8C8C8C' });
            } else {
                return styles;
            }
        };
        this.columnDefs = [
            {
                headerName: 'Name',
                valueGetter: 'data.name',
                colId: 'name',
                tooltipField: 'name',
                width: 268,
                sortable: true,
                comparator: function (v1, v2) {
                    let intv1, intv2 = 1;
                    if ('string' === typeof v1 && v1.trim() !== '') {
                        intv1 = v1.split('').reduce((acc, cur, ind) => acc + cur.charCodeAt(0) / Math.pow(128, ind), 0);
                    }
                    if ('string' === typeof v2 && v2.trim() !== '') {
                        intv2 = v2.split('').reduce((acc, cur, ind) => acc + cur.charCodeAt(0) / Math.pow(128, ind), 0);
                    }
                    return intv1 - intv2;
                },
                menuTabs: [],
                cellStyle: cellStyleHandler
            },
            {
                headerName: 'Type',
                valueGetter: (params) => {
                    if (params.data && params.data.type) {
                        if (params.data.type === EmailDigestType.DASHBOARD) {
                            return 'Dashboard';
                        } else if (params.data.type === EmailDigestType.SCREEN_SETTINGS) {
                            return 'Note';
                        } else {
                            // TAM-34131 Type of email digests shows incorrectly.
                            return 'Note';
                        }
                    }
                    return '';
                },
                colId: 'type',
                tooltipField: 'type',
                width: 160,
                sortable: true,
                menuTabs: [],
                cellStyle: cellStyleHandler
            },
            {
                headerName: 'Description',
                valueGetter: 'data.description',
                colId: 'description',
                tooltipField: 'description',
                width: 536,
                sortable: true,
                menuTabs: [],
                cellStyle: cellStyleHandler
            },
            {
                headerName: 'Schedule at',
                valueGetter: 'data.schedulerStr',
                colId: 'scheduleAt',
                tooltipValueGetter: (params) => {
                    return params.value;
                },
                width: 224,
                sortable: false,
                cellRenderer: EmailDigestSchedulerInfoComponent,
                menuTabs: [],
                cellStyle: cellStyleHandler
            },
            {
                headerName: 'Author',
                valueGetter: 'data.author.shortName',
                colId: 'author',
                tooltipValueGetter: (params) => {
                    return params.value;
                },
                width: 160,
                sortable: true,
                menuTabs: [],
                cellStyle: cellStyleHandler
            },
            {
                headerName: '',
                minWidth: 148,
                width: 148,
                maxWidth: 148,
                cellRenderer: EmailDigestActionsComponent,
                cellRendererParams: {
                    event$: this._gridRowSubject$
                },
                sortable: false,
                menuTabs: [],
            },
            {
                hide: true,
                headerName: 'lastModifiedDate',
                colId: 'lastModifiedDate',
                valueGetter: 'data.lastModifiedDate',
                sort: 'desc',
                cellStyle: cellStyleHandler,
            },
        ];
        this.gridOptions = {
            debug: false,
            defaultColDef: {
                sortable: true,
                resizable: true
            },
            cacheBlockSize: 50,
            colResizeDefault: 'shift',
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            paginationPageSize: DEFAULT_PAGE_SIZE,
            rowSelection: 'single',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns: true,
            getRowHeight: () => 40,
        };
        if (this.collapseSuject) {
            this.collapseSuject.subscribe(event => {
                this._gridColumnAutoFit();
            });
        }
        this.initMeatBall();
        this.initGridContextMenu();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        // set the top of sub-chrome back to 119px (the default value).
        this._layoutService.changeSubTitleTopOpen$.next('119px');
    }

    create() {
        const currentUser = this._storeQuerier.getCurrentUser();
        const userId = currentUser.credential.publishPubConfiguration ? currentUser.id : null;
        const tempDigest = this._emailDigestService.getDefaultEmailDigest(EmailDigestType.SCREEN_SETTINGS, userId);
        this._emailDigestService.dialogActionSubject$.next({
            type: EmailDigestDialogActions.OPEN,
            payload: tempDigest,
        });
    }

    onDisplayedColumnsChanged(event) {
        event.api.sizeColumnsToFit();
    }

    @HostListener('window:resize', ['$event'])
    onResize(e) {
        if (!this._gridApi) {
            return;
        }
        this._gridApi.sizeColumnsToFit();
    }

    onRowSelected(event) {
        const item = event.api.getSelectedNodes()[0];
        if (item) {
            this.selectedItemMap.clear();
            this.selectedItemMap.set(item.data.id, item.rowIndex);
        }
    }

    onGridReady(params) {
        this._gridApi = params.api;
    }

    showContextMenu(e: MouseEvent) {
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(e, ''));
    }

    initMeatBall() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.showItemIcon = false;
        this.contextMenuConfig.items = [
            { id: 'note', text: 'Note Digest' },
            { id: 'dashboard', text: 'Dashboard Digest' }
        ];
        this.contextMenuConfig.events$.subscribe(event => {
            if (event.payload.id === 'note') {
                this.create();
            } else if (event.payload.id === 'dashboard') {
                this._createEmailDigest();
            }
        });
    }

    initGridContextMenu() {
        this.gridContextMenuConfig = new ContextMenuConfig();
        this.gridContextMenuConfig.stateful = false;
        this.gridContextMenuConfig.showItemIcon = false;
        this.gridContextMenuConfig.items = [];
        this.gridContextMenuConfig.events$.subscribe(event => {
            if (event.payload.menuItem && event.payload.menuItem.event) {
                event.payload.menuItem.event();
            }
        });
    }

    private _createEmailDigest() {
        const currentUser = this._storeQuerier.getCurrentUser();
        const userId = currentUser.credential.publishPubConfiguration ? currentUser.id : null;
        const otherSetting = new DashboardPrintSetting();
        const tempDigest = this._emailDigestService.getDefaultEmailDigest(EmailDigestType.DASHBOARD, userId);
        tempDigest.reportData = otherSetting;
        this._emailDigestService.dialogActionSubject$.next({
            type: EmailDigestDialogActions.OPEN,
            payload: tempDigest,
            needGetInfoFromServer: true,
            gridUpdateRow$: this._gridRowSubject$
        });
    }

    private _gridColumnAutoFit() {
        if (this._gridApi) {
            this._gridApi.refreshClientSideRowModel();
            this._gridApi.sizeColumnsToFit();
        }
    }

    private _onCreateEmailDigestDetail(event) {
        if (event.type === EmailDigestDialogEvents.SAVE) {
            this._store.dispatch({
                type: EmailDigestActionTypes.SET_CONFIG,
                payload: lodash.cloneDeep(event.payload),
            });
            const id = 'new';
            this._router.navigate(['email-digest', id]);
        }
    }

    private _onEmailDigestListLoaded(array) {
        for (let i = 0; i < array.length; i++) {
            array[i].schedulerStr = ScheduleHelperService.getScheduledAt(array[i].scheduler);
        }
        this.rowData = array;
    }

    private _editDashboardEmailDigest(event) {
        // must use cloneDeep, because change data.name
        const data = _.cloneDeep(event.payload);
        const otherSetting = new DashboardPrintSetting();
        data.reportData = Object.assign({}, otherSetting, data.reportData);
        if (data.reportData && data.reportData.displaySetting) {
            if (data.reportData.displaySetting.landscape) {
                data.reportData.displaySetting.layout = Layout.LANDSCAPE;
            } else {
                data.reportData.displaySetting.layout = Layout.PORTRAIT;
            }
        }
        if (event.type === EmailDigestDialogActions.COPY) {
            data.name = `Copy of ${data.name}`;
            data.active = true;
        }
        this._emailDigestService.dialogActionSubject$.next({
            type: event.type,
            payload: data,
            needGetInfoFromServer: true,
            gridUpdateRow$: this._gridRowSubject$
        });
    }
}
